/**
 * 图表「数据颜色」token（与 Figma Variables 名称、取值一致）。
 * 同步方式：对节点调用 MCP `get_variable_defs`（fileKey `0xkyur5MwXrYbyfTUf8tF9`，node `457:3861`）。
 * 设计文件：【CLOUD】智能云报表服务；变量集：`数据颜色-100` … `数据颜色-390`（步进 10，共 30 色）。
 * 说明：系列色为稿定固定色值，不随浅色/深色主题切换（与 Figma 该组变量一致）。
 */

/**
 * 按 Figma 变量编号顺序排列（100→390），用于与设计稿变量表对照，非图表轮询序。
 */
export const DATA_CHART_COLOR_SEQUENCE = [
  '#ffcc00', // 数据颜色-100
  '#ff9d00', // 数据颜色-110
  '#ff5c5c', // 数据颜色-120
  '#ff6a00', // 数据颜色-130
  '#e94f37', // 数据颜色-140
  '#ff0000', // 数据颜色-150
  '#ff005d', // 数据颜色-160
  '#d90368', // 数据颜色-170
  '#b83280', // 数据颜色-180
  '#ff00bf', // 数据颜色-190
  '#dd00ff', // 数据颜色-200
  '#a252e9', // 数据颜色-210
  '#5856d6', // 数据颜色-220
  '#7d4cdb', // 数据颜色-230
  '#9000ff', // 数据颜色-240
  '#4c00ff', // 数据颜色-250
  '#007aff', // 数据颜色-260
  '#00a8e8', // 数据颜色-270
  '#477bff', // 数据颜色-280
  '#2274a5', // 数据颜色-290
  '#5ac8fa', // 数据颜色-300
  '#00eeff', // 数据颜色-310
  '#00ffd4', // 数据颜色-320
  '#3ab795', // 数据颜色-330
  '#0fb9b1', // 数据颜色-340
  '#3bceac', // 数据颜色-350
  '#24c069', // 数据颜色-360
  '#36df77', // 数据颜色-370
  '#00ff99', // 数据颜色-380
  '#00ff55', // 数据颜色-390
] as const;

/**
 * 图表系列色轮询序（审美排序，与 Figma 编号序不同；PC/App 共用规则）。
 * 分配：`CHART_PALETTE_ORDERED[i % 30]`，超过 30 条再循环。
 */
/** 1～6：高辨识度核心；7～17：冷暖交错扩展；18～30：荧光/高饱和置后，循环回 0 时有色阶差 */
export const CHART_PALETTE_ORDERED = [
  '#007aff', // 0  数据颜色-260 蓝
  '#ff9d00', // 1  数据颜色-110 橙
  '#24c069', // 2  数据颜色-360 绿
  '#e94f37', // 3  数据颜色-140 橙红
  '#a252e9', // 4  数据颜色-210 紫
  '#00a8e8', // 5  数据颜色-270 青蓝
  '#ffcc00', // 6  数据颜色-100 黄
  '#b83280', // 7  数据颜色-180 玫红
  '#0fb9b1', // 8  数据颜色-340 暗青
  '#ff6a00', // 9  数据颜色-130 亮橙
  '#5856d6', // 10 数据颜色-220 深蓝紫
  '#36df77', // 11 数据颜色-370 薄荷绿
  '#d90368', // 12 数据颜色-170 深粉红
  '#2274a5', // 13 数据颜色-290 海洋蓝
  '#3bceac', // 14 数据颜色-350 碧绿
  '#ff5c5c', // 15 数据颜色-120 珊瑚红
  '#5ac8fa', // 16 数据颜色-300 天空蓝
  '#ff00bf', // 17 数据颜色-190 桃红
  '#00ff55', // 18 数据颜色-390 荧光绿
  '#9000ff', // 19 数据颜色-240 纯紫
  '#00ffd4', // 20 数据颜色-320 荧光青
  '#ff0000', // 21 数据颜色-150 正红
  '#4c00ff', // 22 数据颜色-250 靛蓝
  '#3ab795', // 23 数据颜色-330 莫兰迪绿
  '#ff005d', // 24 数据颜色-160 艳红
  '#00eeff', // 25 数据颜色-310 亮青蓝
  '#dd00ff', // 26 数据颜色-200 亮紫
  '#00ff99', // 27 数据颜色-380 翠绿
  '#7d4cdb', // 28 数据颜色-230 暮光紫
  '#477bff', // 29 数据颜色-280 宝蓝（收尾；循环回 0 时与 260 蓝有色阶差）
] as const;



/** 单条数据系列色的字面量类型（轮询调色板） */
export type DataChartColorHex = (typeof CHART_PALETTE_ORDERED)[number];

/**
 * 营业饼图前五扇区色（{@link CHART_PALETTE_ORDERED} 前 5 项）。
 * @deprecated 请使用 {@link getDataChartSeriesColors} 或 {@link CHART_PALETTE_ORDERED}
 */
export const PIE_CHART_DEFAULT_SLICE_COLORS: readonly DataChartColorHex[] = CHART_PALETTE_ORDERED.slice(
  0,
  5,
) as readonly DataChartColorHex[];

/**
 * 营业折线图前五系列色（{@link CHART_PALETTE_ORDERED} 前 5 项）。
 * @deprecated 请使用 {@link getDataChartSeriesColors} 或 {@link CHART_PALETTE_ORDERED}
 */
export const LINE_CHART_DEFAULT_SERIES_COLORS = CHART_PALETTE_ORDERED.slice(
  0,
  5,
) as readonly DataChartColorHex[];

/**
 * 按系列/扇区顺序从 {@link CHART_PALETTE_ORDERED} 轮询取色；超过 30 条时循环复用。
 * @param seriesCount - 数据系列个数（饼图扇区数、折线条数等），≤0 时返回空数组
 * @returns 与系列顺序对齐的颜色字符串数组
 */
export function getDataChartSeriesColors(seriesCount: number): string[] {
  if (seriesCount <= 0) {
    return [];
  }
  const palette = CHART_PALETTE_ORDERED;
  const paletteLen = palette.length;
  return Array.from({ length: seriesCount }, (_, i) => palette[i % paletteLen]);
}

/** 饼图按 value 名次取色时的单条入参 */
export type PieColorRankItem = {
  /** 扇区数值（金额、占比基数等） */
  value: number;
  /** 展示名；并列时用于稳定排序 */
  label?: string;
  /** 唯一标识；无 label 时作并列排序键 */
  id?: string;
  /** 调用方指定色，优先于名次调色 */
  color?: string;
};

/**
 * 饼图扇区色：按 value 从大到小分配 {@link CHART_PALETTE_ORDERED}（第 1 名 → 下标 0），
 * 返回数组与入参 items 顺序一致；并列 value 按 label/id 字典序稳定排序。
 * @param items - 扇区数据；已传 color 的项保留原色
 */
export function assignPieColorsByValueRank(items: readonly PieColorRankItem[]): string[] {
  if (items.length === 0) {
    return [];
  }
  const palette = CHART_PALETTE_ORDERED;
  const paletteLen = palette.length;
  const indices = items.map((_, i) => i);
  indices.sort((a, b) => {
    const va = Number(items[a].value) || 0;
    const vb = Number(items[b].value) || 0;
    if (vb !== va) {
      return vb - va;
    }
    const la = items[a].label ?? items[a].id ?? String(a);
    const lb = items[b].label ?? items[b].id ?? String(b);
    return la.localeCompare(lb);
  });
  const rankByIndex = new Map<number, number>();
  indices.forEach((origIdx, rank) => {
    rankByIndex.set(origIdx, rank);
  });
  return items.map((item, i) => {
    if (item.color) {
      return item.color;
    }
    const rank = rankByIndex.get(i) ?? i;
    return palette[rank % paletteLen];
  });
}

/**
 * 饼图扇区色（平行数组版）：与 {@link assignPieColorsByValueRank} 规则相同。
 * @param values - 与 labels 对齐的数值
 * @param labels - 可选标签，用于并列稳定排序
 */
export function getPieChartColorsByValues(
  values: readonly number[],
  labels?: readonly string[],
): string[] {
  const items: PieColorRankItem[] = values.map((value, i) => ({
    value,
    label: labels?.[i] ?? String(i),
  }));
  return assignPieColorsByValueRank(items);
}

/**
 * 按 value 从大到小重排饼图平行数组（与 App 端 d3 饼图默认扇区序一致；并列时按 label 稳定排序）。
 * @param values - 扇区数值
 * @param labels - 扇区标签
 * @param parallel - 与扇区等长的附加平行数组（如图例文案），按相同规则重排
 * @returns 重排后的 values、labels 与 parallel
 */
export function sortPieChartParallelByValueDesc(
  values: readonly number[],
  labels: readonly string[],
  parallel: readonly string[][] = [],
): {
  values: number[];
  labels: string[];
  parallel: string[][];
} {
  if (values.length === 0) {
    return { values: [], labels: [], parallel: parallel.map(() => []) };
  }
  /** 原下标列表，按 value 降序重排。 */
  const indices = values.map((_, i) => i);
  indices.sort((a, b) => {
    const va = Number(values[a]) || 0;
    const vb = Number(values[b]) || 0;
    if (vb !== va) {
      return vb - va;
    }
    return (labels[a] ?? '').localeCompare(labels[b] ?? '');
  });
  return {
    values: indices.map((i) => Number(values[i]) || 0),
    labels: indices.map((i) => labels[i] ?? ''),
    parallel: parallel.map((arr) => indices.map((i) => arr[i] ?? '')),
  };
}

/**
 * 折线图系列色（与 {@link getDataChartSeriesColors} 相同，保留别名便于调用方迁移）。
 * @param seriesCount - 折线条数，≤0 时返回空数组
 */
export function getLineChartSeriesColors(seriesCount: number): string[] {
  return getDataChartSeriesColors(seriesCount);
}

